import { useState } from 'react'

import { CloseButton, Menu } from '@mantine/core'
import { DatePicker } from '@mantine/dates'
import dayjs from 'dayjs'
import { Calendar } from 'lucide-react'

import DNButton from 'src/components/Buttons/Button/DNButton'
import StyledMenu from 'src/components/Overlays/StyledMenu/StyledMenu'

const buildPreSelectedDateRanges = () => [
  {
    label: 'Last 2 days',
    value: 'last2Days',
    start: dayjs().subtract(2, 'day').startOf('day').toDate(),
    end: new Date(),
  },
  {
    label: 'Last 7 days',
    value: 'last7Days',
    start: dayjs().subtract(7, 'day').startOf('day').toDate(),
    end: new Date(),
  },
  {
    label: 'Last 30 days',
    value: 'last30Days',
    start: dayjs().subtract(30, 'day').startOf('day').toDate(),
    end: new Date(),
  },
  {
    label: 'This month',
    value: 'thisMonth',
    start: dayjs().startOf('month').toDate(),
    end: new Date(),
  },
  {
    label: 'Last month',
    value: 'lastMonth',
    start: dayjs().subtract(1, 'month').startOf('month').toDate(),
    end: dayjs().subtract(1, 'month').endOf('month').toDate(),
  },
]

const DateRangeMenu = ({
  startDate,
  endDate,
  handleChange,
  clearable = false,
  minDate = null,
  ...props
}) => {
  const [showCalendar, setShowCalendar] = useState(false)
  const [opened, setOpened] = useState(false)

  const minDay = minDate ? dayjs(minDate).startOf('day') : null

  const applyRangeSelection = (start: Date | string, end?: Date | string) => {
    let startTime = dayjs(start).startOf('day')
    const endTime = end
      ? dayjs(end).endOf('day')
      : dayjs(start).endOf('day')

    if (minDay && startTime.isBefore(minDay, 'day')) {
      startTime = minDay
    }

    handleChange([startTime.toDate(), endTime.toDate()])
    setOpened(false)
    setShowCalendar(false)
  }

  const availablePresets = buildPreSelectedDateRanges()
    .map((dateRange) => {
      if (!minDay) {
        return dateRange
      }

      const clampedStart = dayjs(dateRange.start).isBefore(minDay, 'day')
        ? minDay.toDate()
        : dateRange.start

      return { ...dateRange, start: clampedStart }
    })
    .filter((dateRange) => !minDay || !dayjs(dateRange.end).isBefore(minDay, 'day'))

  return (
    <StyledMenu
      closeOnItemClick={false}
      opened={opened}
      onChange={setOpened}
      shadow="md"
      width={300}
      position="bottom-end"
      withArrow
      arrowPosition="center"
    >
      <Menu.Target>
        <DNButton onClick={() => setShowCalendar(false)}>
          <Calendar
            size={14}
            className="mx-1 fill-none stroke-doubleNickel-gray-700"
          />
          {startDate && endDate
            ? dayjs(startDate).format('MM/DD/YYYY') ===
              dayjs(endDate).format('MM/DD/YYYY')
              ? dayjs(startDate).format('MM/DD/YYYY')
              : `${dayjs(startDate).format('MM/DD/YYYY')} - ${
                  dayjs(endDate).format('MM/DD/YYYY') !== 'Invalid Date'
                    ? dayjs(endDate).format('MM/DD/YYYY')
                    : ''
                }`
            : 'Date Range'}
          {clearable && startDate && endDate && (
            <CloseButton
              className="ml-2"
              size={'sm'}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                handleChange([null, null])
                setOpened(false)
              }}
            />
          )}
        </DNButton>
      </Menu.Target>
      <Menu.Dropdown>
        {showCalendar ? (
          <DatePicker
            className="flex flex-1 items-center justify-center"
            type="range"
            allowSingleDateInRange
            size="sm"
            minDate={minDate}
            maxDate={new Date()}
            onChange={(value) => {
              const [start, end] = value
              if (!start) {
                return
              }

              if (!end) {
                return
              }

              applyRangeSelection(start, end)
            }}
            {...props}
          />
        ) : (
          <>
            {availablePresets.map((dateRange) => (
              <Menu.Item
                key={dateRange.value}
                onClick={() => {
                  applyRangeSelection(dateRange.start, dateRange.end)
                }}
              >
                {dateRange.label}
              </Menu.Item>
            ))}
            <Menu.Item
              onClick={(e) => {
                e.stopPropagation()
                setShowCalendar(true)
              }}
            >
              Select from calendar
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </StyledMenu>
  )
}

export default DateRangeMenu
